import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../api/client';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend,
} from 'recharts';
import { ArrowLeft, Printer, Download, Loader2, Pencil, Save, X, Trash2, Trophy, Target, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import PostureImageUpload from '../../components/PostureImageUpload';

// V2 5개 항목 정의 (백엔드 FIT_REPORT_AREAS와 동일 순서)
const AREAS = [
  { key: 'strength',           score_field: 'strength_score',          label: '근력' },
  { key: 'muscular_endurance', score_field: 'endurance_score',         label: '근지구력' },
  { key: 'cardio',             score_field: 'cardio_score',            label: '심폐지구력' },
  { key: 'balance',            score_field: 'body_balance_score',      label: '체형' },
  { key: 'body_composition',   score_field: 'body_composition_score',  label: '체성분' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'completed', label: 'Completed' },
  { value: 'shared', label: 'Shared' },
];

const STATUS_STYLE = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  shared: 'bg-blue-50 text-blue-700 border-blue-200',
};

// FIT REPORT 전용 5단계 Level (백엔드 _fit_report_level과 동일)
const fitLevel = (score) => {
  if (score === null || score === undefined || score === '' || Number.isNaN(Number(score))) return null;
  const v = Number(score);
  if (v < 30) return 'Level 0';
  if (v < 60) return 'Level 1';
  if (v < 80) return 'Level 2';
  if (v < 95) return 'Level 3';
  return 'Level 4';
};

const LEVEL_SHORT = { 'Level 0': 'L0', 'Level 1': 'L1', 'Level 2': 'L2', 'Level 3': 'L3', 'Level 4': 'L4' };

const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s.length === 10 ? s + 'T00:00:00' : s);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
};

const fmtDiff = (d) => {
  if (d === null || d === undefined) return '-';
  if (d > 0) return `+${d}`;
  return `${d}`;
};

// =============================================================================

export default function FitReportDetail() {
  const { reportId } = useParams();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editable copies
  const [score, setScore] = useState(null);
  const [inbody, setInbody] = useState(null);
  const [posture, setPosture] = useState({ image_data_url: null, comment: '', improvement_points: '' });
  const [notes, setNotes] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [status, setStatus] = useState('draft');

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/admin/fit-reports/${reportId}`);
      setReport(res.data);
      setScore(res.data.score_snapshot || {});
      setInbody(res.data.inbody_snapshot || {});
      setPosture(res.data.posture_snapshot || { image_data_url: null, comment: '', improvement_points: '' });
      setNotes(res.data.notes || '');
      setReportDate(res.data.report_date || '');
      setStatus(res.data.status || 'draft');
    } catch (e) {
      console.error(e);
      toast.error('리포트를 불러오지 못했습니다');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reportId]);

  const handleStatusChange = async (newStatus) => {
    setStatus(newStatus);
    try {
      await apiClient.patch(`/admin/fit-reports/${reportId}`, { status: newStatus });
      toast.success(`상태가 ${newStatus}로 변경되었습니다`);
      setReport((prev) => prev && { ...prev, status: newStatus });
    } catch (e) {
      toast.error('상태 변경에 실패했습니다');
      setStatus(report?.status || 'draft');
    }
  };

  const recomputeTotal = () => {
    if (!score) return;
    const arr = AREAS
      .map((a) => score[a.score_field])
      .filter((v) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v)))
      .map(Number);
    const total = arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
    setScore((prev) => ({ ...prev, total_score: total === null ? null : Math.round(total * 10) / 10 }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        report_date: reportDate,
        notes,
        score_snapshot: score,
        inbody_snapshot: inbody,
        posture_snapshot: posture,
      };
      const res = await apiClient.patch(`/admin/fit-reports/${reportId}`, payload);
      setReport(res.data.report);
      setEditing(false);
      toast.success('리포트가 저장되었습니다');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '저장에 실패했습니다');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('이 FIT REPORT를 삭제하시겠습니까?')) return;
    try {
      await apiClient.delete(`/admin/fit-reports/${reportId}`);
      toast.success('리포트가 삭제되었습니다');
      navigate('/admin/fit-reports');
    } catch (e) {
      toast.error('삭제에 실패했습니다');
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadPdf = async () => {
    try {
      toast.info('PDF 생성 중...');
      const res = await apiClient.get(`/admin/fit-reports/${reportId}/pdf`, { responseType: 'blob' });
      const cd = res.headers?.['content-disposition'] || '';
      let filename = `FIT_REPORT_${reportId}.pdf`;
      const m1 = cd.match(/filename\*=UTF-8''([^;]+)/i);
      if (m1) { try { filename = decodeURIComponent(m1[1]); } catch (_) {} }
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF가 다운로드되었습니다');
    } catch (e) {
      console.error(e);
      toast.error('PDF 생성에 실패했습니다');
    }
  };

  // Derived data
  const currentScore = editing ? score : (report?.score_snapshot || {});
  const currentInbody = editing ? inbody : (report?.inbody_snapshot || {});
  const currentPosture = editing ? posture : (report?.posture_snapshot || { image_data_url: null, comment: '', improvement_points: '' });
  const comparison = report?.comparison_snapshot || {};
  const roadmap = report?.roadmap_snapshot || {};

  // Page 1 radar: single
  const page1RadarData = useMemo(() => AREAS.map((a) => ({
    subject: a.label,
    value: Number(currentScore?.[a.score_field] ?? 0) || 0,
  })), [currentScore]);

  // Page 2 radar: 회원 vs 평균
  const page2RadarData = useMemo(() => AREAS.map((a) => {
    const my = currentScore?.[a.score_field];
    const avg = comparison?.averages?.[a.key];
    return {
      subject: a.label,
      나: Number(my ?? 0) || 0,
      평균: Number(avg ?? 0) || 0,
    };
  }), [currentScore, comparison]);

  // Page 3 radar: 현재 vs 3개월 (6개월은 레이더에 표시하지 않음)
  const page3RadarData = useMemo(() => AREAS.map((a) => {
    const cur = currentScore?.[a.score_field];
    const it = (roadmap?.items || []).find((x) => x.key === a.key) || {};
    return {
      subject: a.label,
      현재: Number(cur ?? 0) || 0,
      '3개월 목표': Number(it.three_month ?? 0) || 0,
    };
  }), [currentScore, roadmap]);

  // =============================================================================

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[40vh] text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> 불러오는 중...
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-6 text-center text-gray-500">
        리포트를 찾을 수 없습니다.
        <div className="mt-3">
          <Button variant="outline" onClick={() => navigate('/admin/fit-reports')}>목록으로</Button>
        </div>
      </div>
    );
  }

  const m = report.member_snapshot || {};

  return (
    <div className="bg-gray-50 min-h-screen print:bg-white" data-testid="fit-report-detail">
      {/* Toolbar - hidden on print */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/fit-reports')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> 목록
            </Button>
            <span className={
              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ' +
              (STATUS_STYLE[report.status] || STATUS_STYLE.draft)
            }>
              {report.status}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Select value={status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-32" data-testid="select-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`status-option-${o.value}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!editing ? (
              <>
                <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="btn-edit">
                  <Pencil className="w-4 h-4 mr-1" /> 수정
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="btn-print">
                  <Printer className="w-4 h-4 mr-1" /> 인쇄
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownloadPdf} data-testid="btn-pdf">
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
                <Button
                  variant="ghost" size="sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={handleDelete}
                  data-testid="btn-delete-report"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => { setEditing(false); load(); }} data-testid="btn-cancel-edit">
                  <X className="w-4 h-4 mr-1" /> 취소
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="btn-save-edit"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} 저장
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* PAGE 1 */}
      <ReportPage data-testid="page-1">
        <PageHeader
          subtitle="현재 나의 체력 상태"
          pageNum={1}
        />

        {/* Member info row */}
        <MemberRow m={m} report={report} editing={editing} reportDate={reportDate} setReportDate={setReportDate} />

        {/* FIT SCORE: 페이지 상단, 가운데 정렬 */}
        <div className="text-center mt-8">
          <div className="text-xs tracking-[0.3em] text-orange-500 font-semibold">FIT SCORE</div>
          <div className="text-7xl md:text-8xl font-bold text-gray-900 leading-none mt-2" data-testid="fit-score">
            {currentScore?.total_score ?? '-'}
            <span className="text-2xl text-gray-400 ml-2">점</span>
          </div>
          <p className="text-sm text-gray-600 mt-4 leading-relaxed max-w-lg mx-auto">
            INSIGHT FIT는 종합 등급이 아닌 <b>5가지 영역의 균형</b>을 중요시합니다.
            아래 항목별 점수와 Level을 확인하세요.
          </p>
        </div>

        {/* Radar: 중앙 배치, 확대 */}
        <div className="mt-6 flex justify-center">
          <div className="w-full max-w-[640px] h-[34rem] md:h-[40rem]" data-testid="page1-radar">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={page1RadarData} outerRadius="80%">
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: '#1F2937', fontSize: 16, fontWeight: 700 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                <Radar name="현재" dataKey="value" stroke="#F97316" fill="#F97316" fillOpacity={0.45} strokeWidth={2.5} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 5 area cards — Level 강조 */}
        <div className="mt-8">
          <div className="text-sm font-semibold text-gray-700 mb-3 tracking-wide">항목별 점수</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {AREAS.map((a) => {
              const v = currentScore?.[a.score_field];
              const lvl = fitLevel(v);
              return (
                <div
                  key={a.key}
                  className="rounded-xl p-4 border border-orange-100 bg-gradient-to-br from-orange-50/40 to-white text-center"
                  data-testid={`score-card-${a.score_field}`}
                >
                  <div className="text-xs text-gray-500 font-medium tracking-wide">{a.label}</div>
                  {editing ? (
                    <Input
                      type="number" step="0.1" min="0"
                      value={v ?? ''}
                      onChange={(e) => setScore((p) => ({ ...p, [a.score_field]: e.target.value === '' ? null : Number(e.target.value) }))}
                      onBlur={recomputeTotal}
                      className="mt-2 h-9 text-center"
                    />
                  ) : (
                    <>
                      {/* Level 강조 - 가장 크게 */}
                      <div
                        className={
                          'mt-3 inline-flex flex-col items-center justify-center px-3 py-2 rounded-xl border-2 ' +
                          (lvl
                            ? 'border-orange-300 bg-orange-100 text-orange-700'
                            : 'border-gray-200 bg-gray-50 text-gray-400')
                        }
                        data-testid={`level-${a.score_field}`}
                      >
                        <span className="text-xs font-semibold tracking-[0.15em] opacity-80">{lvl ? 'LEVEL' : ''}</span>
                        <span className="text-2xl md:text-3xl font-bold leading-none mt-0.5">
                          {lvl ? lvl.replace('Level ', '') : '미측정'}
                        </span>
                      </div>
                      {/* 점수: Level 아래 작게 */}
                      <div className="mt-2 text-sm text-gray-500">
                        <span className="font-semibold text-gray-700 tabular-nums">{v ?? '-'}</span>
                        {v !== null && v !== undefined && v !== '' && <span className="ml-0.5">점</span>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Level Guide — 보조 안내 카드 */}
        <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50/60 px-4 py-3" data-testid="level-guide">
          <div className="text-xs font-semibold text-gray-600 tracking-wide mb-2">LEVEL GUIDE</div>
          <div className="grid grid-cols-5 gap-2 text-center text-xs">
            {[
              { lv: 'Level 4', desc: '상위 5%' },
              { lv: 'Level 3', desc: '상위 20%' },
              { lv: 'Level 2', desc: '평균 이상' },
              { lv: 'Level 1', desc: '평균' },
              { lv: 'Level 0', desc: '하위 40%' },
            ].map((row) => (
              <div key={row.lv} className="bg-white border border-gray-100 rounded-md py-2">
                <div className="font-semibold text-gray-700">{row.lv}</div>
                <div className="text-gray-500 mt-0.5">{row.desc}</div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-gray-500 mt-2 leading-relaxed">
            Level은 0~4까지 총 5단계로 구성됩니다. 회원님의 최근 측정 데이터를 기반으로 자동 계산됩니다.
          </div>
        </div>
      </ReportPage>

      {/* PAGE 2 */}
      <ReportPage data-testid="page-2">
        <PageHeader title="나는 평균보다 어떤가?" subtitle="동일 성별·연령 평균 대비 나의 위치" pageNum={2} />

        {!comparison?.has_data ? (
          <div className="rounded-lg border border-dashed border-gray-200 p-12 text-center text-gray-400 mt-6" data-testid="page2-empty">
            평균 비교 데이터가 부족합니다. <br />
            <span className="text-xs">기준표 관리 페이지에서 연령 평균값을 먼저 설정해주세요.</span>
          </div>
        ) : (
          <>
            <div className="mt-4 flex justify-center">
              <div className="w-full max-w-[680px] h-[36rem] md:h-[42rem]" data-testid="page2-radar">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={page2RadarData} outerRadius="78%">
                    <PolarGrid stroke="#E5E7EB" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: '#1F2937', fontSize: 16, fontWeight: 700 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                    <Radar name="나" dataKey="나" stroke="#F97316" fill="#F97316" fillOpacity={0.45} strokeWidth={2.5} />
                    <Radar name="평균" dataKey="평균" stroke="#94A3B8" fill="#94A3B8" fillOpacity={0.2} strokeWidth={2} strokeDasharray="6 3" />
                    <Legend wrapperStyle={{ paddingTop: 10 }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Comparison table */}
            <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white" data-testid="page2-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">항목</th>
                    <th className="text-right px-4 py-3 font-medium">나</th>
                    <th className="text-right px-4 py-3 font-medium">평균</th>
                    <th className="text-right px-4 py-3 font-medium">차이</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(comparison.items || []).map((it) => (
                    <tr key={it.key} data-testid={`page2-row-${it.key}`}>
                      <td className="px-4 py-3 font-medium text-gray-900">{it.label}</td>
                      <td className="px-4 py-3 text-right text-gray-900 font-semibold">{it.my ?? '-'}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{it.average ?? '-'}</td>
                      <td className={
                        'px-4 py-3 text-right font-semibold ' +
                        (it.diff === null || it.diff === undefined
                          ? 'text-gray-400'
                          : it.diff > 0 ? 'text-emerald-600' : it.diff < 0 ? 'text-red-500' : 'text-gray-600')
                      }>{fmtDiff(it.diff)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* TOP 2 sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-5 h-5 text-emerald-600" />
                  <div className="font-semibold text-emerald-800">강점 TOP 2</div>
                </div>
                {(comparison.top_strengths || []).length > 0 ? (
                  <div className="space-y-2" data-testid="top-strengths">
                    {(comparison.top_strengths || []).map((s, idx) => (
                      <div key={s.key} className="flex items-center bg-white rounded-lg px-3 py-2.5 border border-emerald-100">
                        <span className="font-medium text-gray-800 text-base">{idx === 0 ? '🥇' : '🥈'} {s.label}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">평균 비교 데이터가 부족합니다.</div>
                )}
              </div>
              <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-5 h-5 text-orange-600" />
                  <div className="font-semibold text-orange-800">성장 우선순위 TOP 2</div>
                </div>
                {(comparison.top_priorities || []).length > 0 ? (
                  <div className="space-y-2" data-testid="top-priorities">
                    {(comparison.top_priorities || []).map((s, idx) => (
                      <div key={s.key} className="flex items-center bg-white rounded-lg px-3 py-2.5 border border-orange-100">
                        <span className="font-medium text-gray-800 text-base">{idx === 0 ? '①' : '②'} {s.label}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">평균 비교 데이터가 부족합니다.</div>
                )}
              </div>
            </div>
          </>
        )}
      </ReportPage>

      {/* PAGE 3 */}
      <ReportPage data-testid="page-3">
        <PageHeader title="FIT LEVEL ROADMAP" subtitle="성장 로드맵" pageNum={3} />

        <div className="mt-3 text-sm text-gray-600 leading-relaxed">
          현재 위치를 확인했습니다. 이제 다음 성장 목표를 확인해보세요. <br />
          <span className="text-gray-500">아래 목표 영역은 INSIGHT FIT 회원 평균 성장 데이터를 기반으로 계산되었습니다.</span>
        </div>

        {/* Radar: 현재 vs 3개월 (6개월 미표시) — 중앙 배치, 확대 */}
        <div className="mt-4 flex justify-center">
          <div className="w-full max-w-[680px] h-[36rem] md:h-[42rem]" data-testid="page3-radar">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={page3RadarData} outerRadius="78%">
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: '#1F2937', fontSize: 16, fontWeight: 700 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                <Radar name="현재" dataKey="현재" stroke="#94A3B8" fill="#94A3B8" fillOpacity={0.25} strokeWidth={2} />
                <Radar name="3개월 목표" dataKey="3개월 목표" stroke="#F97316" fill="#F97316" fillOpacity={0.45} strokeWidth={2.5} />
                <Legend wrapperStyle={{ paddingTop: 10 }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Growth target table */}
        <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white" data-testid="page3-target-table">
          <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-600 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4" /> 성장 목표 (점수)
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white text-gray-600 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-2 font-medium">항목</th>
                <th className="text-right px-4 py-2 font-medium">현재</th>
                <th className="text-right px-4 py-2 font-medium">3개월</th>
                <th className="text-right px-4 py-2 font-medium">6개월</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(roadmap.items || []).map((it) => (
                <tr key={it.key} data-testid={`page3-target-row-${it.key}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{it.label}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{it.current ?? '-'}</td>
                  <td className="px-4 py-3 text-right text-orange-600 font-semibold">{it.three_month ?? '-'}</td>
                  <td className="px-4 py-3 text-right text-orange-700 font-bold">{it.six_month ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Level change table */}
        <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white" data-testid="page3-level-table">
          <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-600">Level 변화</div>
          <table className="w-full text-sm">
            <thead className="bg-white text-gray-600 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-2 font-medium">항목</th>
                <th className="text-center px-4 py-2 font-medium">현재</th>
                <th className="text-center px-4 py-2 font-medium">3개월</th>
                <th className="text-center px-4 py-2 font-medium">6개월</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(roadmap.items || []).map((it) => (
                <tr key={it.key} data-testid={`page3-level-row-${it.key}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{it.label}</td>
                  <td className="px-4 py-3 text-center text-gray-700">{LEVEL_SHORT[it.current_level] || '-'}</td>
                  <td className="px-4 py-3 text-center text-orange-600 font-semibold">{LEVEL_SHORT[it.three_month_level] || '-'}</td>
                  <td className="px-4 py-3 text-center text-orange-700 font-bold">{LEVEL_SHORT[it.six_month_level] || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportPage>

      {/* Coach edit-only: inbody / posture / notes — shown only in editing mode (not in print) */}
      {editing && (
        <div className="max-w-5xl mx-auto p-6 space-y-4 print:hidden bg-white border-t-8 border-orange-50">
          <div className="text-xs tracking-[0.25em] text-gray-500 font-semibold">COACH ONLY (수정 모드)</div>
          <div className="text-lg font-semibold text-gray-900">신체 분석 / 메모 (수정 영역)</div>

          {/* Inbody edit */}
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm font-medium text-gray-700 mb-3">인바디</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'weight', label: '체중 (kg)' },
                { key: 'skeletal_muscle_mass', label: '골격근량 (kg)' },
                { key: 'body_fat_percentage', label: '체지방률 (%)' },
                { key: 'inbody_score', label: '인바디 점수' },
              ].map((f) => (
                <div key={f.key}>
                  <Label className="text-xs text-gray-600">{f.label}</Label>
                  <Input
                    type="number" step="0.1"
                    value={currentInbody?.[f.key] ?? ''}
                    onChange={(e) => setInbody((p) => ({ ...p, [f.key]: e.target.value === '' ? null : Number(e.target.value) }))}
                    className="mt-1 h-9"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Posture edit */}
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm font-medium text-gray-700 mb-3">체형</div>
            <PostureImageUpload value={currentPosture.image_data_url} onChange={(v) => setPosture((p) => ({ ...p, image_data_url: v }))} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-xs text-gray-600">체형 코멘트</Label>
                <Textarea rows={3} value={currentPosture.comment || ''} onChange={(e) => setPosture((p) => ({ ...p, comment: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs text-gray-600">개선 포인트</Label>
                <Textarea rows={3} value={currentPosture.improvement_points || ''} onChange={(e) => setPosture((p) => ({ ...p, improvement_points: e.target.value }))} />
              </div>
            </div>
          </div>

          {/* Notes edit */}
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm font-medium text-gray-700 mb-2">상담 메모</div>
            <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
      )}

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white; }
          .print\\:hidden { display: none !important; }
          .print\\:bg-white { background: white !important; }
          .report-page { page-break-after: always; break-after: page; min-height: auto !important; }
          .report-page:last-child { page-break-after: auto; break-after: auto; }
        }
      `}</style>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function ReportPage({ children, ...rest }) {
  return (
    <div
      className="report-page max-w-5xl mx-auto px-6 py-8 my-6 bg-white rounded-2xl shadow-sm print:shadow-none print:my-0 print:rounded-none print:py-6 print:max-w-none print:mx-0"
      {...rest}
    >
      {children}
    </div>
  );
}

function PageHeader({ title, subtitle, pageNum }) {
  return (
    <div className="flex items-end justify-between pb-4 border-b border-gray-100">
      <div>
        <div className="text-xs tracking-[0.3em] text-orange-500 font-semibold">INSIGHT FIT</div>
        <h1 className="text-3xl font-bold text-gray-900 mt-1">{title || 'FIT REPORT'}</h1>
        {subtitle && <div className="text-sm text-gray-500 mt-1">{subtitle}</div>}
      </div>
      <div className="text-xs text-gray-400 font-medium tabular-nums">PAGE {pageNum} / 3</div>
    </div>
  );
}

function MemberRow({ m, report, editing, reportDate, setReportDate }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
      <div>
        <div className="text-xs text-gray-500">회원명</div>
        <div className="text-lg font-semibold text-gray-900 mt-0.5">{m?.name || '-'}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">성별 · 나이</div>
        <div className="text-base text-gray-800 mt-0.5">
          {m?.gender === 'male' ? '남' : m?.gender === 'female' ? '여' : '-'} · {m?.age ?? '-'}세
        </div>
      </div>
      <div>
        <div className="text-xs text-gray-500">전화번호</div>
        <div className="text-base text-gray-800 mt-0.5">{m?.phone || '-'}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">Report Date</div>
        {editing ? (
          <Input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} className="mt-1 h-9" />
        ) : (
          <div className="text-base text-gray-800 mt-0.5">{fmtDate(report?.report_date)}</div>
        )}
      </div>
    </div>
  );
}
